import { Component, OnDestroy, OnInit, Renderer2 } from "@angular/core";
import { Router, NavigationEnd, NavigationStart } from "@angular/router";
import { AuthService } from "@auth0/auth0-angular";
import { localStorageKey, LOGOUT_MESSAGE } from "@core/constants";
import { SSOService } from "@core/services/sso.service";
import { environment as ENV } from "@environments/environment";
import { TranslateService } from "@ngx-translate/core";
import { Subscription, interval } from "rxjs";
import { filter, switchMap } from "rxjs/operators";
import { WebAdapterService } from "@core/web-adapter/web-adapter.service";

declare var gtag;

const getCleanQueryString = (searchString: string) => {
  return searchString.split("&").reduce(function (prev, curr, i, arr) {
    var p = curr.split("=");
    if (p[0]) {
      prev[decodeURIComponent(p[0])] = decodeURIComponent(p[1]);
    }
    return prev;
  }, {});
};

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
})
export class AppComponent implements OnInit, OnDestroy {
  subscription: Subscription;
  private navigateEvent: () => void;
  constructor(
    private ssoService: SSOService,
    private authService: AuthService,
    private router: Router,
    private renderer: Renderer2,
    private waService: WebAdapterService
  ) {
    // Setup Google Analytics
    const navEndEvents$ = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd)
    );
    navEndEvents$.subscribe((event: NavigationEnd) => {
      gtag("config", "G-VEQ2RMTS2Z", {
        page_path: event.urlAfterRedirects,
      });
    });

    // Set redirect path on refresh
    this.subscription = router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        const browserRefresh = !router.navigated;
        if (browserRefresh) {
          const searchString = window.location.search.substring(
            1,
            window.location.search.length
          );
          const searchObj = getCleanQueryString(searchString);
          const redirect = {
            path: window.location.pathname,
            query: searchObj,
          };
          if (redirect.path !== "/") {
            localStorage.setItem("redirect", JSON.stringify(redirect));
          }
        }
      } else if (event instanceof NavigationEnd) {
        this.waService.removeAllScripts();
        this.waService.removeAllStyles();
      }
    });
  }

  public isLoading = true;

  public navigateToStoredRedirect() {
    // Redirect to saved redirect URL if any, otherwise go home
    const redirectStorageData = localStorage.getItem("redirect");
    try {
      const redirectData = JSON.parse(redirectStorageData);
      if (redirectData?.path) {
        localStorage.removeItem("redirect");
        this.router.navigate([redirectData.path], {
          queryParams: redirectData.query,
        });
      } else {
        const path = window.location.pathname;
        this.router.navigate([path]);
      }
    } catch (e) {
      const path = window.location.pathname;
      this.router.navigate([path]);
    }
  }

  public logout() {
    localStorage.clear();
    this.authService.logout({
      logoutParams: { returnTo: ENV.auth0.redirectUri },
    });
  }

  public init() {
    // ADD global navigation from mf to others modules
    this.navigateEvent = this.renderer.listen("window", "navigate", (event) => {
      const { path, ...options } = event.detail;
      this.router.navigate([path], options);
    });

    // Handle logout event (from outside router to avoid flickering; router works as fallback)
    if (window.location.pathname === "/logout") {
      alert(LOGOUT_MESSAGE);
      this.logout();
    } else {
      const storageData = { baseUrl: ENV.gateway.base_url };
      return this.authService.idTokenClaims$.subscribe(
        (token) => {
          // Get token, if there is none, force logout
          const ssoToken: string = token?.__raw;
          const email: string = token?.email;
          storageData["tokenClaims"] = ssoToken;
          // Fetch user data. If there is none, redirect to self-onboarding
          this.ssoService.fetchUserData(ssoToken).subscribe(
            (userData) => {
              storageData["user"] = userData;
              storageData["dataUser"] = userData;
              localStorage.setItem(
                localStorageKey,
                JSON.stringify(storageData)
              );
              window.parent.postMessage({ type: "FINISH_LOAD_USER_DATA" }, "*");
              this.navigateToStoredRedirect();
              this.isLoading = false;
            },
            () => {
              // No user data, redirect to self onboarding
              const emptyUserData = { permissions: [], email };
              storageData["user"] = emptyUserData;
              storageData["dataUser"] = emptyUserData;
              localStorage.setItem(
                localStorageKey,
                JSON.stringify(storageData)
              );
              this.router.navigate(["/self-onboarding"]);
              this.isLoading = false;
            }
          );
        },
        (error) => {
          this.logout();
        }
      );
    }
  }

  scheduleTokenRefresh() {
    const REFRESH_INTERVAL = 8 * 60 * 60 * 1000; // get a new token every 8 hours
    interval(REFRESH_INTERVAL)
      .pipe(
        switchMap(() =>
          this.authService.getAccessTokenSilently({ cacheMode: "off" })
        )
      )
      .subscribe(
        (token) => {
          const storageData = JSON.parse(localStorage.getItem(localStorageKey));
          storageData.tokenClaims = token;
          localStorage.setItem(localStorageKey, JSON.stringify(storageData));
        },
        (error) => {
          console.error("Error refreshing token", error);
        }
      );
  }

  saveUrlAndParams(): void {
    const route = window.location.href;
    if (route.split("/dashboard/").length > 1) {
      const queryParams = new URLSearchParams(
        route.split("/dashboard/")[1].split("?")[1]
      );
      const result: { [key: string]: string | string[] } = {};
      queryParams?.forEach((value, key) => {
        if (result[key]) {
          if (Array.isArray(result[key])) {
            (result[key] as string[]).push(value);
          } else {
            result[key] = [result[key] as string, value];
          }
        } else {
          result[key] = value;
        }
      });
      const redirectToPage = {
        redirect: true,
        url: `dashboard/${route.split("/dashboard/")[1]?.split("?")[0]}`,
        queryParams: result,
      };
      localStorage.setItem("redirect_to_page", JSON.stringify(redirectToPage));
    }
  }

  async ngOnInit() {
    this.init();
    this.scheduleTokenRefresh();
    this.saveUrlAndParams();
  }

  ngOnDestroy(): void {
    this.navigateEvent && this.navigateEvent(); // Remove global navigation
  }
}
